import React, { Component } from "react";
import {
  Row, Col, Button, Card, Form, Badge, ListGroup, Offcanvas, Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import configData from "../../config.json";
import WidgetInstanceEditor from "./WidgetInstanceEditor";
import LayoutPreview from "./LayoutPreview";

/**
 * LayoutEditor - Full page layout editor with inline widget editing.
 *
 * Features:
 *   - Save full layout chain (page → rows → cells) in one API call
 *   - Inline widget instance editing via Offcanvas
 *   - Quick-create widget from left panel
 *   - Row reordering (up/down)
 *   - Cell colSpan adjustment with visual Bootstrap grid
 *   - Auto-create widget from table name
 *
 * Props:
 *   - pageRoute: Route to edit layout for (null for new)
 *   - onSave: Callback after save
 *   - onClose: Callback to close
 */
export default class LayoutEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      saving: false,
      saveStep: "",
      sId: sessionStorage.getItem("sessionId"),
      uId: sessionStorage.getItem("uId"),
      // Page layout
      pageRoute: props.pageRoute || "",
      pageTitle: "",
      pageIcon: "",
      layoutType: "standard",
      pageLayoutId: null,
      // Rows and cells
      rows: [],
      // Widget library
      widgetInstances: [],
      widgetTypes: [],
      // Widget editor offcanvas
      showWidgetEditor: false,
      editingWidgetId: null, // null = create new
      // Quick-create from table
      quickCreateTable: "",
      quickCreateLoading: false,
      // Layout preview
      showPreview: false,
    };
  }

  componentDidMount() {
    this.fetchWidgetInstances();
    this.fetchWidgetTypes();
    if (this.props.pageRoute) {
      this.fetchPageConfig();
    } else {
      this.setState({ loading: false, rows: [this.createNewRow(1)] });
    }
  }

  // ==================== DATA FETCHING ====================

  fetchPageConfig = () => {
    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);
    formData.append("pageRoute", this.props.pageRoute);

    fetch(configData.api_url + "C_Widget_framework/get_page_config", {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success" && data.pageConfig) {
          const pc = data.pageConfig;
          this.setState({
            loading: false,
            pageRoute: pc.pageRoute,
            pageTitle: pc.pageTitle || "",
            pageIcon: pc.pageIcon || "",
            layoutType: pc.layoutType || "standard",
            pageLayoutId: pc.page_layouts_Id,
            rows: (pc.rows || []).map((r) => ({
              id: r.layout_rows_Id,
              rowOrder: parseInt(r.rowOrder),
              rowHeight: r.rowHeight || "auto",
              cssClass: r.cssClass || "",
              cells: (r.cells || []).map((c) => ({
                id: c.layout_cells_Id,
                cellOrder: parseInt(c.cellOrder),
                colSpan: parseInt(c.colSpan) || 12,
                widgetInstanceId: c.widget_instances_Id || "",
                instanceName: c.instanceName || "",
                typeName: c.typeName || "",
                cssClass: c.cssClass || "",
              })),
            })),
          });
        } else {
          this.setState({ loading: false, rows: [this.createNewRow(1)] });
        }
      })
      .catch(() => {
        this.setState({ loading: false });
        toast.error("Failed to load page config");
      });
  };

  fetchWidgetInstances = () => {
    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);

    fetch(configData.api_url + "C_Widget_instances/list", {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          this.setState({ widgetInstances: data.list || [] });
        }
      })
      .catch(() => {});
  };

  fetchWidgetTypes = () => {
    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);

    fetch(configData.api_url + "C_Widget_types/list", {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          this.setState({ widgetTypes: data.list || [] });
        }
      })
      .catch(() => {});
  };

  // ==================== ROW / CELL OPERATIONS ====================

  createNewRow = (order) => ({
    id: null,
    rowOrder: order,
    rowHeight: "auto",
    cssClass: "",
    cells: [],
  });

  createNewCell = (order) => ({
    id: null,
    cellOrder: order,
    colSpan: 12,
    widgetInstanceId: "",
    instanceName: "",
    typeName: "",
    cssClass: "",
  });

  addRow = () => {
    const rows = [...this.state.rows];
    rows.push(this.createNewRow(rows.length + 1));
    this.setState({ rows });
  };

  removeRow = (rowIndex) => {
    if (!window.confirm("Remove this row and all its cells?")) return;
    const rows = this.state.rows.filter((_, i) => i !== rowIndex);
    rows.forEach((r, i) => (r.rowOrder = i + 1));
    this.setState({ rows });
  };

  addCell = (rowIndex) => {
    const rows = [...this.state.rows];
    const row = { ...rows[rowIndex] };
    row.cells = [...row.cells, this.createNewCell(row.cells.length + 1)];
    rows[rowIndex] = row;
    this.setState({ rows });
  };

  removeCell = (rowIndex, cellIndex) => {
    const rows = [...this.state.rows];
    const row = { ...rows[rowIndex] };
    row.cells = row.cells.filter((_, i) => i !== cellIndex);
    row.cells.forEach((c, i) => (c.cellOrder = i + 1));
    rows[rowIndex] = row;
    this.setState({ rows });
  };

  updateCell = (rowIndex, cellIndex, field, value) => {
    const rows = [...this.state.rows];
    const row = { ...rows[rowIndex] };
    row.cells = [...row.cells];
    const updatedCell = { ...row.cells[cellIndex], [field]: value };

    // When widget is selected, update instanceName for display
    if (field === "widgetInstanceId") {
      const wi = this.state.widgetInstances.find(
        (w) => String(w.widget_instances_Id) === String(value)
      );
      updatedCell.instanceName = wi ? wi.instanceName : "";
    }

    row.cells[cellIndex] = updatedCell;
    rows[rowIndex] = row;
    this.setState({ rows });
  };

  moveRow = (fromIndex, direction) => {
    const toIndex = fromIndex + direction;
    if (toIndex < 0 || toIndex >= this.state.rows.length) return;
    const rows = [...this.state.rows];
    [rows[fromIndex], rows[toIndex]] = [rows[toIndex], rows[fromIndex]];
    rows.forEach((r, i) => (r.rowOrder = i + 1));
    this.setState({ rows });
  };

  // ==================== SAVE (FULL CHAIN) ====================

  handleSave = () => {
    if (this.state.isSubmitting) return;
    this.setState({ isSubmitting: true });
    if (!this.state.pageRoute) {
      toast.error("Page route is required");
      this.setState({ isSubmitting: false });
      return;
    }
    if (!this.state.pageTitle) {
      toast.error("Page title is required");
      this.setState({ isSubmitting: false });
      return;
    }

    this.setState({ saving: true, saveStep: "Saving layout..." });

    // Build the full layout payload
    const layoutData = {
      pageRoute: this.state.pageRoute,
      pageTitle: this.state.pageTitle,
      pageIcon: this.state.pageIcon,
      layoutType: this.state.layoutType,
      layoutConfig: "{}",
      page_layouts_Id: this.state.pageLayoutId || null,
      rows: this.state.rows.map((row, ri) => ({
        layout_rows_Id: row.id || null,
        rowOrder: ri + 1,
        rowHeight: row.rowHeight || "auto",
        cssClass: row.cssClass || "",
        cells: (row.cells || []).map((cell, ci) => ({
          layout_cells_Id: cell.id || null,
          cellOrder: ci + 1,
          colSpan: parseInt(cell.colSpan) || 12,
          widget_instances_Id: cell.widgetInstanceId || "0",
          cssClass: cell.cssClass || "",
        })),
      })),
    };

    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);
    formData.append("layoutData", JSON.stringify(layoutData));

    fetch(configData.api_url + "C_Widget_framework/save_full_layout", {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          toast.success("Layout saved successfully!");
          // Clear sessionStorage cache for this page
          sessionStorage.removeItem("wf_page_" + this.state.pageRoute);
          // Update the pageLayoutId in state
          this.setState({
            saving: false,
            saveStep: "",
            pageLayoutId: data.pageLayoutId,
          });
          // Reload the layout to get fresh IDs from DB
          if (this.props.pageRoute) {
            this.fetchPageConfig();
          }
          if (this.props.onSave) this.props.onSave();
        } else {
          toast.error(data.message || "Failed to save layout");
          this.setState({ saving: false, saveStep: "" });
        }
      })
      .catch(() => {
        toast.error("Network error while saving layout");
        this.setState({ saving: false, saveStep: "" });
      }).finally(() => this.setState({ isSubmitting: false }));
  };

  // ==================== WIDGET EDITOR (OFFCANVAS) ====================

  openWidgetEditor = (widgetInstanceId) => {
    this.setState({
      showWidgetEditor: true,
      editingWidgetId: widgetInstanceId || null,
    });
  };

  closeWidgetEditor = () => {
    this.setState({ showWidgetEditor: false, editingWidgetId: null });
  };

  handleWidgetSaved = () => {
    this.closeWidgetEditor();
    // Refresh widget instances list to pick up changes
    this.fetchWidgetInstances();
    toast.success("Widget instance saved");
  };

  // ==================== QUICK CREATE FROM TABLE ====================

  handleQuickCreate = () => {
    if (this.state.isSubmitting) return;
    this.setState({ isSubmitting: true });
    const tableName = this.state.quickCreateTable.trim().toLowerCase();
    if (!tableName) {
      toast.error("Enter a table name");
      return;
    }

    this.setState({ quickCreateLoading: true });

    // Fetch columns from the table
    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);
    formData.append("tableName", tableName);

    fetch(configData.api_url + "masters/getcolumns", {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success" && data.columns) {
          const columns = data.columns.filter(
            (c) => c !== "createdBy" && c !== "updatedBy" && c !== "status" && c !== "createdOn" && c !== "updatedOn"
          );
          const idField = tableName + "_Id";
          const PascalName = "C_" + tableName.charAt(0).toUpperCase() + tableName.slice(1);

          // Build columnsConfig
          const columnsConfig = columns.map((col) => ({
            field: col,
            header: col === idField ? "S.NO" : col.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()),
            sortable: true,
            type: "text",
          }));

          // Build formConfig (exclude PK and system fields)
          const formFields = columns
            .filter((c) => c !== idField)
            .map((col) => ({
              fieldName: col,
              displayName: col.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()),
              inputType: "text",
              required: false,
            }));

          // Create widget instance
          const wiFormData = new FormData();
          wiFormData.append("sId", this.state.sId);
          wiFormData.append("uId", this.state.uId);
          wiFormData.append("instanceName", tableName + "_table");
          wiFormData.append("widget_types_Id", "1"); // data_table_rdt
          wiFormData.append("apiEndPoint", PascalName + "/list");
          wiFormData.append("apiMethod", "POST");
          wiFormData.append("apiParams", "");
          wiFormData.append("columnsConfig", JSON.stringify(columnsConfig));
          wiFormData.append("actionsConfig", JSON.stringify({
            canAdd: true,
            canEdit: true,
            canDelete: true,
            addEndpoint: PascalName + "/add",
            editEndpoint: PascalName + "/update",
            deleteEndpoint: PascalName + "/delete",
            permissionPrefix: tableName,
          }));
          wiFormData.append("tableConfig", JSON.stringify({
            pagination: true,
            paginationPerPage: 100,
            searchable: true,
            idField: idField,
          }));
          wiFormData.append("formConfig", JSON.stringify({ fields: formFields }));
          wiFormData.append("chartConfig", "{}");
          wiFormData.append("filterConfig", "{}");
          wiFormData.append("customProps", "{}");
          wiFormData.append("sourceTable", tableName);
          wiFormData.append("isActive", "1");

          fetch(configData.api_url + "C_Widget_instances/add", {
            method: "POST",
            mode: "cors",
            body: wiFormData,
          })
            .then((res) => res.json())
            .then((wiData) => {
              this.setState({ quickCreateLoading: false, quickCreateTable: "" });
              if (wiData.status === "success") {
                toast.success("Widget '" + tableName + "_table' created with " + columns.length + " columns");
                this.fetchWidgetInstances();
              } else {
                toast.error(wiData.message || "Failed to create widget");
              }
            })
            .catch(() => {
              this.setState({ quickCreateLoading: false });
              toast.error("Network error");
            });
        } else {
          this.setState({ quickCreateLoading: false });
          toast.error("Could not fetch columns for table: " + tableName);
        }
      })
      .catch(() => {
        this.setState({ quickCreateLoading: false });
        toast.error("Network error");
      }).finally(() => this.setState({ isSubmitting: false }));
  };

  // ==================== HELPER ====================

  getWidgetDisplayName = (widgetInstanceId) => {
    const wi = this.state.widgetInstances.find(
      (w) => String(w.widget_instances_Id) === String(widgetInstanceId)
    );
    return wi ? wi.instanceName : "";
  };

  getColSpanTotal = (cells) => {
    return (cells || []).reduce((sum, c) => sum + (parseInt(c.colSpan) || 0), 0);
  };

  // ==================== RENDER ====================

  render() {
    if (this.state.loading) {
      return (
        <div className="text-center p-4">
          <Spinner size="sm" className="me-2" />
          Loading layout editor...
        </div>
      );
    }

    return (
      <div className="layout-editor">
        {/* Page Settings */}
        <Card className="mb-3">
          <Card.Header className="py-2">
            <i className="bi bi-gear me-1"></i> Page Settings
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: "0.85rem" }}>Page Route</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={this.state.pageRoute}
                    onChange={(e) => this.setState({ pageRoute: e.target.value })}
                    placeholder="e.g., testtable"
                    disabled={!!this.props.pageRoute}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: "0.85rem" }}>Page Title</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={this.state.pageTitle}
                    onChange={(e) => this.setState({ pageTitle: e.target.value })}
                    placeholder="Display title"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: "0.85rem" }}>Icon</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    value={this.state.pageIcon}
                    onChange={(e) => this.setState({ pageIcon: e.target.value })}
                    placeholder="bi bi-..."
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: "0.85rem" }}>Layout Type</Form.Label>
                  <Form.Select
                    size="sm"
                    value={this.state.layoutType}
                    onChange={(e) => this.setState({ layoutType: e.target.value })}
                  >
                    <option value="standard">Standard</option>
                    <option value="dashboard">Dashboard</option>
                    <option value="fullwidth">Full Width</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Layout Canvas */}
        <Row>
          {/* Left Panel: Widget Library + Quick Create */}
          <Col md={3}>
            {/* Quick Create from Table */}
            <Card className="mb-2">
              <Card.Header className="py-1 px-2" style={{ fontSize: "0.85rem" }}>
                <i className="bi bi-lightning me-1"></i> Quick Create Widget
              </Card.Header>
              <Card.Body className="p-2">
                <div className="d-flex gap-1">
                  <Form.Control
                    type="text"
                    size="sm"
                    value={this.state.quickCreateTable}
                    onChange={(e) => this.setState({ quickCreateTable: e.target.value })}
                    placeholder="Table name..."
                    style={{ fontSize: "0.8rem" }}
                    onKeyDown={(e) => e.key === "Enter" && this.handleQuickCreate()}
                  />
                  <Button
                    variant="success"
                    size="sm"
                    onClick={this.handleQuickCreate}
                    disabled={this.state.quickCreateLoading}
                    style={{ whiteSpace: "nowrap", fontSize: "0.75rem" }}
                  >
                    {this.state.quickCreateLoading ? (
                      <Spinner size="sm" />
                    ) : (
                      <><i className="bi bi-plus"></i> Create</>
                    )}
                  </Button>
                </div>
                <div className="text-muted mt-1" style={{ fontSize: "0.7rem" }}>
                  Enter DB table name to auto-create a DataTable widget
                </div>
              </Card.Body>
            </Card>

            {/* Widget Library */}
            <Card className="mb-3">
              <Card.Header className="py-1 px-2 d-flex justify-content-between align-items-center">
                <span style={{ fontSize: "0.85rem" }}>
                  <i className="bi bi-puzzle me-1"></i> Widgets
                  <Badge bg="secondary" className="ms-1" style={{ fontSize: "0.65rem" }}>
                    {this.state.widgetInstances.length}
                  </Badge>
                </span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  style={{ fontSize: "0.7rem", padding: "0 6px" }}
                  onClick={() => this.openWidgetEditor(null)}
                >
                  <i className="bi bi-plus"></i> New
                </Button>
              </Card.Header>
              <Card.Body style={{ maxHeight: "400px", overflowY: "auto", padding: "0" }}>
                <ListGroup variant="flush">
                  {this.state.widgetInstances.map((wi) => (
                    <ListGroup.Item
                      key={wi.widget_instances_Id}
                      className="d-flex justify-content-between align-items-center"
                      style={{ fontSize: "0.8rem", padding: "4px 8px" }}
                    >
                      <span>
                        <Badge
                          bg="info"
                          className="me-1"
                          style={{ fontSize: "0.65rem" }}
                        >
                          {wi.widget_types_Id}
                        </Badge>
                        {wi.instanceName}
                      </span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        style={{ fontSize: "0.65rem", padding: "0 4px", lineHeight: "1.2" }}
                        onClick={() => this.openWidgetEditor(wi.widget_instances_Id)}
                        title="Edit widget config"
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                    </ListGroup.Item>
                  ))}
                  {this.state.widgetInstances.length === 0 && (
                    <div className="text-muted text-center py-3" style={{ fontSize: "0.85rem" }}>
                      No widget instances found.
                      <br />
                      Use Quick Create or + New above.
                    </div>
                  )}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>

          {/* Right Panel: Rows & Cells */}
          <Col md={9}>
            {this.state.rows.map((row, rowIndex) => (
              <Card key={rowIndex} className="mb-2" style={{ border: "2px dashed #dee2e6" }}>
                <Card.Header className="d-flex justify-content-between align-items-center py-1 px-2" style={{ backgroundColor: "#f0f4f8" }}>
                  <span style={{ fontSize: "0.85rem" }}>
                    <i className="bi bi-distribute-vertical me-1"></i>
                    Row {row.rowOrder}
                    {row.cells.length > 0 && (
                      <Badge
                        bg={this.getColSpanTotal(row.cells) === 12 ? "success" : "warning"}
                        className="ms-2"
                        style={{ fontSize: "0.65rem" }}
                      >
                        {this.getColSpanTotal(row.cells)}/12 cols
                      </Badge>
                    )}
                  </span>
                  <div>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-1"
                      style={{ fontSize: "0.75rem", padding: "0 4px" }}
                      onClick={() => this.moveRow(rowIndex, -1)}
                      disabled={rowIndex === 0}
                      title="Move up"
                    >
                      <i className="bi bi-arrow-up"></i>
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      className="me-1"
                      style={{ fontSize: "0.75rem", padding: "0 4px" }}
                      onClick={() => this.moveRow(rowIndex, 1)}
                      disabled={rowIndex === this.state.rows.length - 1}
                      title="Move down"
                    >
                      <i className="bi bi-arrow-down"></i>
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-1"
                      style={{ fontSize: "0.75rem", padding: "0 6px" }}
                      onClick={() => this.addCell(rowIndex)}
                    >
                      <i className="bi bi-plus"></i> Cell
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      style={{ fontSize: "0.75rem", padding: "0 4px" }}
                      onClick={() => this.removeRow(rowIndex)}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-2">
                  <Row>
                    {row.cells.map((cell, cellIndex) => (
                      <Col key={cellIndex} md={parseInt(cell.colSpan) || 12} className="mb-1">
                        <Card
                          style={{
                            backgroundColor: cell.widgetInstanceId ? "#e8f5e9" : "#f8f9fa",
                            minHeight: "70px",
                            borderColor: cell.widgetInstanceId ? "#a5d6a7" : "#dee2e6",
                          }}
                        >
                          <Card.Body className="p-2">
                            {/* Widget selector + delete */}
                            <div className="d-flex justify-content-between align-items-center mb-1">
                              <Form.Select
                                size="sm"
                                value={cell.widgetInstanceId || ""}
                                onChange={(e) =>
                                  this.updateCell(rowIndex, cellIndex, "widgetInstanceId", e.target.value)
                                }
                                style={{ fontSize: "0.78rem" }}
                              >
                                <option value="">-- Select Widget --</option>
                                {this.state.widgetInstances.map((wi) => (
                                  <option key={wi.widget_instances_Id} value={wi.widget_instances_Id}>
                                    {wi.instanceName}
                                  </option>
                                ))}
                              </Form.Select>
                              {cell.widgetInstanceId && (
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  className="ms-1"
                                  style={{ fontSize: "0.65rem", padding: "0 4px" }}
                                  onClick={() => this.openWidgetEditor(cell.widgetInstanceId)}
                                  title="Edit widget config"
                                >
                                  <i className="bi bi-pencil"></i>
                                </Button>
                              )}
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="ms-1"
                                style={{ fontSize: "0.65rem", padding: "0 4px" }}
                                onClick={() => this.removeCell(rowIndex, cellIndex)}
                                title="Remove cell"
                              >
                                <i className="bi bi-x"></i>
                              </Button>
                            </div>
                            {/* ColSpan + CSS */}
                            <div className="d-flex gap-1 align-items-center">
                              <Form.Control
                                type="number"
                                size="sm"
                                min="1"
                                max="12"
                                value={cell.colSpan}
                                onChange={(e) =>
                                  this.updateCell(rowIndex, cellIndex, "colSpan", e.target.value)
                                }
                                style={{ width: "60px", fontSize: "0.78rem" }}
                                title="Column span (1-12)"
                              />
                              <span className="text-muted" style={{ fontSize: "0.7rem" }}>
                                col-{cell.colSpan}
                              </span>
                              {cell.instanceName && (
                                <Badge bg="light" text="dark" style={{ fontSize: "0.65rem" }}>
                                  {cell.instanceName}
                                </Badge>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                    {row.cells.length === 0 && (
                      <Col className="text-center text-muted py-3" style={{ fontSize: "0.85rem" }}>
                        Empty row. Click "+ Cell" to add a widget cell.
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
            ))}

            {/* Action Buttons */}
            <div className="d-flex gap-2 mt-2">
              <Button variant="outline-primary" onClick={this.addRow}>
                <i className="bi bi-plus me-1"></i> Add Row
              </Button>
              <Button
                variant="success"
                onClick={this.handleSave}
                disabled={this.state.saving}
              >
                {this.state.saving ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    {this.state.saveStep || "Saving..."}
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-1"></i>
                    Save Layout
                  </>
                )}
              </Button>
              {this.state.pageLayoutId && (
                <Button
                  variant="outline-info"
                  onClick={() => this.setState({ showPreview: true })}
                  title="Preview the rendered page"
                >
                  <i className="bi bi-eye me-1"></i>
                  Preview
                </Button>
              )}
              {this.props.onClose && (
                <Button variant="outline-secondary" onClick={this.props.onClose}>
                  Cancel
                </Button>
              )}
            </div>

            {/* Help Text */}
            <div className="mt-3 p-2 bg-light rounded" style={{ fontSize: "0.78rem" }}>
              <strong>Tips:</strong>
              <ul className="mb-0 mt-1">
                <li>Each row uses a 12-column Bootstrap grid. Set cell colSpan values to sum to 12 per row.</li>
                <li>Full-width widget = colSpan 12. Two side-by-side = 6 + 6. Three = 4 + 4 + 4.</li>
                <li>Use <strong>Quick Create</strong> to auto-generate a DataTable widget from any existing table.</li>
                <li>Click the <i className="bi bi-pencil"></i> icon on a widget to edit its columns, form fields, and config.</li>
              </ul>
            </div>
          </Col>
        </Row>

        {/* Layout Preview Modal */}
        <LayoutPreview
          pageRoute={this.state.pageRoute}
          show={this.state.showPreview}
          onClose={() => this.setState({ showPreview: false })}
        />

        {/* Widget Instance Editor Offcanvas */}
        <Offcanvas
          show={this.state.showWidgetEditor}
          onHide={this.closeWidgetEditor}
          placement="end"
          style={{ width: "650px" }}
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title>
              {this.state.editingWidgetId
                ? "Edit Widget Instance"
                : "Create New Widget Instance"}
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            {this.state.showWidgetEditor && (
              <WidgetInstanceEditor
                instanceId={this.state.editingWidgetId}
                widgetTypes={this.state.widgetTypes}
                onSave={this.handleWidgetSaved}
                onClose={this.closeWidgetEditor}
              />
            )}
          </Offcanvas.Body>
        </Offcanvas>
      </div>
    );
  }
}
